import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, GradientBorder } from '@/components/ui';
import { AppLogo } from '@/components/icons/AppLogo';

const FIREBASE_API_KEY = import.meta.env.VITE_FIREBASE_API_KEY;

// Firebase Identity Toolkit "confirm email verification" endpoint — the same call the
// Firebase SDK's `applyActionCode` makes under the hood. Called directly here so we don't
// have to take a `firebase` dependency or widen the published auth-client package.
const CONFIRM_VERIFICATION_URL = `https://identitytoolkit.googleapis.com/v1/accounts:update?key=${FIREBASE_API_KEY}`;

type Status = 'idle' | 'verifying' | 'success' | 'error';

/** Firebase Identity Toolkit error codes → human-readable copy. */
const ERROR_COPY: Record<string, string> = {
  EXPIRED_OOB_CODE: 'This verification link has expired. Request a new one from the app.',
  INVALID_OOB_CODE: 'This verification link is invalid or has already been used.',
};

const GENERIC_ERROR = 'We could not verify your email. Please try again.';

/**
 * `/verify-email` route.
 *
 * Lands the user from the verification email and confirms their address **only on an
 * explicit click** — never on page load. The email links here (carrying the Firebase
 * `oobCode`) instead of to Firebase's hosted action handler, which applies the code the
 * instant the page is rendered. That auto-apply is what let email security scanners
 * (Safe Links, link prefetchers) silently verify addresses; gating it behind a real
 * button press defeats them, since a scanner renders the page but never clicks.
 *
 * Confirming flips `emailVerified` in Firebase; the signup tab's `watchEmailVerification`
 * poll then mirrors it into our backend and advances the funnel — so this page does not
 * need an authenticated session of its own (the email may be opened on another device).
 */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const oobCode = params.get('oobCode');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  async function handleVerify() {
    if (!oobCode) return;
    setStatus('verifying');
    setError(null);
    try {
      const res = await fetch(CONFIRM_VERIFICATION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ oobCode }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        const code = body?.error?.message?.split(' ')[0] ?? '';
        throw new Error(ERROR_COPY[code] ?? GENERIC_ERROR);
      }
      setStatus('success');
      // Return the user to the platform automatically (ARC-145). Try to close the
      // tab first — that drops them onto the original signup tab, which the watcher
      // has already advanced. Browsers only honor window.close() for script-opened
      // windows, so when the tab stays put we navigate this tab to the app instead.
      // Delayed so the confirmation is seen.
      setTimeout(() => {
        window.close();
        setTimeout(() => window.location.assign('/'), 400);
      }, 1500);
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : GENERIC_ERROR);
    }
  }

  const verified = status === 'success';
  const missingCode = !oobCode;

  return (
    <div className="relative flex min-h-svh flex-col items-center justify-center overflow-hidden px-6 py-10">
      <VerifyEmailKeyframes />

      {/* Atmospheric brand glows behind the card — soft, on-palette, never busy. */}
      <span
        aria-hidden
        className="pointer-events-none absolute -top-24 -right-20 size-72 rounded-full bg-accent/25 blur-[90px]"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute -bottom-28 -left-24 size-80 rounded-full bg-purple/20 blur-[100px]"
      />

      <div className="mb-8" style={{ animation: 've-fade-up 0.5s ease-out both' }}>
        <AppLogo width={150} />
      </div>

      <div
        className="relative w-full max-w-[400px] rounded-[24px] border border-border bg-surface px-7 py-10 shadow-card"
        style={{ animation: 've-fade-up 0.6s ease-out 0.06s both' }}
      >
        <div className="flex flex-col items-center text-center" role="status" aria-live="polite">
          <StatusBadge status={status} missingCode={missingCode} />

          <h1
            className="mt-7 text-[26px] font-bold leading-tight text-primary"
            style={{ animation: 've-fade-up 0.5s ease-out 0.16s both' }}
          >
            {verified ? "You're verified" : missingCode ? 'Link incomplete' : 'Verify your email'}
          </h1>

          <p
            className="mt-3 max-w-[300px] text-[15px] leading-relaxed text-muted"
            style={{ animation: 've-fade-up 0.5s ease-out 0.22s both' }}
          >
            {verified
              ? 'Your email is confirmed. You can safely close this tab and return to Crown Prediction to start trading.'
              : missingCode
                ? 'This link is missing its verification code. Open the most recent verification email and tap the button again.'
                : "You're one tap away — confirm your email address to start trading on the markets."}
          </p>

          {!verified && !missingCode && (
            <div className="mt-8 w-full" style={{ animation: 've-fade-up 0.5s ease-out 0.28s both' }}>
              <Button
                variant="default"
                size="lg"
                className="w-full"
                onClick={handleVerify}
                disabled={status === 'verifying'}
              >
                {status === 'verifying' ? 'Verifying…' : status === 'error' ? 'Try again' : 'Confirm my email'}
              </Button>

              {error && (
                <p className="mt-4 text-[13px] leading-relaxed text-danger-alt" role="alert">
                  {error}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <p className="mt-6 text-[13px] text-muted" style={{ animation: 've-fade-up 0.5s ease-out 0.34s both' }}>
        Crown Prediction
      </p>
    </div>
  );
}

/**
 * The hero icon badge — the page's focal point. Uses the brand accent gradient ring
 * while pending, then resolves to a semantic green / red badge. Each state animates in
 * (pop + glow) so the transition reads as a deliberate, satisfying confirmation.
 */
function StatusBadge({ status, missingCode }: { status: Status; missingCode: boolean }) {
  if (status === 'success') {
    return (
      <Badge glowClass="bg-success/40" ringClass="border-2 border-success/40 bg-success/10">
        <svg viewBox="0 0 24 24" fill="none" className="size-11 text-success" aria-hidden>
          <path
            d="M4.5 12.5l5 5 10-11"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength={1}
            style={{ strokeDasharray: 1, strokeDashoffset: 1, animation: 've-draw 0.55s ease-out 0.18s forwards' }}
          />
        </svg>
      </Badge>
    );
  }

  if (status === 'error' || missingCode) {
    return (
      <Badge glowClass="bg-danger/35" ringClass="border-2 border-danger/40 bg-danger/10">
        <svg viewBox="0 0 24 24" fill="none" className="size-10 text-danger" aria-hidden>
          <path d="M12 8v5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          <circle cx="12" cy="16.5" r="1.3" fill="currentColor" />
          <path d="M12 3.5l9 15.5H3l9-15.5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        </svg>
      </Badge>
    );
  }

  // idle + verifying — brand accent ring.
  return (
    <div className="relative grid place-items-center" style={{ animation: 've-pop 0.5s ease-out both' }}>
      <span
        aria-hidden
        className="absolute inset-0 rounded-full bg-accent/30 blur-2xl"
        style={{ animation: 've-glow 3s ease-in-out infinite' }}
      />
      <GradientBorder borderWidth={2.5} className="relative grid size-24 place-items-center rounded-full bg-input-soft">
        {status === 'verifying' ? (
          <svg viewBox="0 0 24 24" fill="none" className="size-9 animate-spin text-accent-dark" aria-hidden>
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2.4" />
            <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" className="size-10 text-accent-dark" aria-hidden>
            <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" strokeWidth="2" />
            <path
              d="M4 7.5l8 5.5 8-5.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </GradientBorder>
    </div>
  );
}

function Badge({ glowClass, ringClass, children }: { glowClass: string; ringClass: string; children: ReactNode }) {
  return (
    <div className="relative grid place-items-center" style={{ animation: 've-pop 0.5s ease-out both' }}>
      <span
        aria-hidden
        className={`absolute inset-0 rounded-full blur-2xl ${glowClass}`}
        style={{ animation: 've-glow 3s ease-in-out infinite' }}
      />
      <div className={`relative grid size-24 place-items-center rounded-full ${ringClass}`}>{children}</div>
    </div>
  );
}

/** Scoped keyframes — no motion library in this app, so animations are CSS-only. */
function VerifyEmailKeyframes() {
  return (
    <style>{`
      @keyframes ve-fade-up { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
      @keyframes ve-pop { 0% { opacity: 0; transform: scale(0.6); } 60% { opacity: 1; transform: scale(1.08); } 100% { transform: scale(1); } }
      @keyframes ve-glow { 0%, 100% { opacity: 0.45; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.1); } }
      @keyframes ve-draw { to { stroke-dashoffset: 0; } }
      @media (prefers-reduced-motion: reduce) {
        [style*="ve-"] { animation: none !important; }
      }
    `}</style>
  );
}
